import {
  motion,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import { useRef, useEffect, useState } from "react";
import trailer from "../../assets/videos/droneVideo.MP4";

export default function HeroSection() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  /* ================= DEVICE CHECK ================= */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  /* ================= HEAVILY SMOOTHED SCROLL ================= */
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 60,
    damping: 30,
    mass: 1,
  });

  /* ================= DESKTOP TRANSFORMS ================= */
  const maskXDesktop = useTransform(
    smoothProgress,
    [0, 0.6],
    ["0%", "-260%"]
  );

  const overlayXDesktop = useTransform(
    smoothProgress,
    [0, 0.6],
    ["0%", "260%"]
  );

  /* ================= MOBILE / TABLET TRANSFORMS ================= */
  const maskYMobile = useTransform(
    smoothProgress,
    [0, 0.6],
    ["0%", "-120%"]
  );

  const overlayXMobile = useTransform(
    smoothProgress,
    [0, 0.6],
    ["0%", "120%"]
  );

  const maskRadius = useTransform(
    smoothProgress,
    [0, 0.6],
    ["32px", "0px"]
  );

  const leftOpacity = useTransform(
    smoothProgress,
    [0.25, 0.6],
    [1, 0]
  );

  /* ================= VIDEO CONTROL (NO SOUND LOGIC) ================= */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const keepPlaying = () => {
      if (video.paused) {
        video.play().catch(() => {});
      }
    };

    window.addEventListener("scroll", keepPlaying);
    document.addEventListener("visibilitychange", keepPlaying);

    return () => {
      window.removeEventListener("scroll", keepPlaying);
      document.removeEventListener("visibilitychange", keepPlaying);
    };
  }, []);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative h-[200vh] bg-[#fcfcfc]"
    >
      <div className="sticky top-0 h-screen overflow-hidden relative">

        {/* VIDEO */}
        <div className="absolute inset-0 z-0 will-change-transform translate-z-0">
          <video
            ref={videoRef}
            src={trailer}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover pointer-events-none"
          />
        </div>

        {/* LEFT PANEL */}
        <motion.div
          style={{
            x: isMobile ? 0 : maskXDesktop,
            y: isMobile ? maskYMobile : 0,
            opacity: leftOpacity,
          }}
          className="
            absolute
            left-0 top-0
            lg:w-1/2 w-full
            lg:h-full h-1/2
            z-20
            px-6 lg:px-20
            flex flex-col
            lg:justify-center
          "
        >
          <div className="mt-auto lg:mt-0 pb-6">
            <p className="text-lg font-medium text-gray-200 uppercase tracking-wide mb-6 ml-3">
              The Journey Begins
            </p>

            <h1 className="text-[50px] sm:text-[100px] lg:text-[180px] leading-[0.8] font-extrabold uppercase text-white">
              BROADBOY
            </h1>
          </div>

          <p className="max-w-lg text-lg text-gray-400 ml-3 pb-6">
            Every step, every climb, every road a raw pursuit of adventure,
            stories, and moments carved in motion.
          </p>
        </motion.div>

        {/* MASK */}
        <motion.div
          style={{
            x: isMobile ? 0 : maskXDesktop,
            y: isMobile ? maskYMobile : 0,
            borderRadius: maskRadius,
          }}
          className="
            absolute
            left-0 top-0
            lg:w-1/2 w-full
            lg:h-full h-1/2
            bg-[#0e202b]
            z-10
          "
        />

        {/* RIGHT OVERLAY */}
        <motion.div
          style={{
            x: isMobile ? overlayXMobile : overlayXDesktop,
            opacity: leftOpacity,
          }}
          className="
            absolute
            right-0
            bottom-12
            z-50
            w-auto
          "
        >
          <div className="inline-block whitespace-nowrap bg-[#0e202b] rounded-tl-[24px] px-8 py-6 shadow-lg">
            <h2 className="text-2xl lg:text-3xl font-semibold text-gray-200 text-right leading-tight">
              ADVENTURE
              <span className="block md:hidden" />
              THAT INSPIRES
            </h2>
          </div>
        </motion.div>

        {/* BOTTOM BASE BAR */}
        <motion.div className="absolute right-0 bottom-0 w-full z-40">
          <div className="bg-[#0e202b] px-8 py-6 shadow-lg" />
        </motion.div>

      </div>
    </section>
  );
}
